import { useEffect } from 'react';
import { Alert, Button, Form, Grid, Input, InputNumber, Message } from '@arco-design/web-react';
import { PageHeader, MoneyInput } from '@/components/ui';
import { authApi } from '@/api';
import { useAuthStore } from '@/store/authStore';
import { EMAIL_REGEX, PHONE_INTL_REGEX } from '@/utils/validators';
import { centsToDollars, dollarsToCents, formatMoney, formatPercent } from '@/utils/money';

const { Row, Col } = Grid;

export default function ProfileSettingsView() {
  const { profile, patchProfile, refreshProfile } = useAuthStore();
  const [contactForm] = Form.useForm<{
    email: string;
    phone: string;
    displayName: string;
    legalName: string;
    addressLine: string;
  }>();
  const [pwdForm] = Form.useForm<{
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>();
  const [feeForm] = Form.useForm<{
    depositFeeRateBps: number;
    cardOpenFeeCents: number;
  }>();

  const minRate = profile?.depositFeeRateBps ?? 0;
  const minOpen = profile?.cardOpenFeeCents ?? 0;

  useEffect(() => {
    if (profile) {
      contactForm.setFieldsValue({
        email: profile.email ?? '',
        phone: profile.phone ?? '',
        displayName: profile.displayName ?? '',
        legalName: profile.legalName ?? '',
        addressLine: profile.addressLine ?? '',
      });
      feeForm.setFieldsValue({
        depositFeeRateBps:
          profile.defaultDownstreamDepositFeeRateBps ?? profile.depositFeeRateBps ?? 0,
        cardOpenFeeCents: centsToDollars(
          profile.defaultDownstreamCardOpenFeeCents ?? profile.cardOpenFeeCents ?? 0,
        ),
      });
    }
  }, [profile, contactForm, feeForm]);

  const handleContactSubmit = async () => {
    try {
      const v = await contactForm.validate();
      const next = await authApi.updateContact({
        email: v.email,
        phone: v.phone || undefined,
        displayName: v.displayName || undefined,
        legalName: v.legalName || undefined,
        addressLine: v.addressLine || undefined,
      });
      patchProfile(next);
      Message.success('个人资料已更新');
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const v = await pwdForm.validate();
      if (v.newPassword !== v.confirmPassword) {
        Message.error('两次输入的新密码不一致');
        return;
      }
      await authApi.changePassword({
        oldPassword: v.oldPassword,
        newPassword: v.newPassword,
      });
      Message.success('密码已更新，请使用新密码重新登录');
      pwdForm.resetFields();
      refreshProfile();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const handleFeeSubmit = async () => {
    try {
      const v = await feeForm.validate();
      if (v.depositFeeRateBps < minRate) {
        Message.error(`默认入账费率不得低于本人费率 ${formatPercent(minRate)}`);
        return;
      }
      const openCents = dollarsToCents(v.cardOpenFeeCents);
      if (openCents < minOpen) {
        Message.error(`默认开卡费不得低于本人开卡费 ${formatMoney(minOpen)}`);
        return;
      }
      const next = await authApi.updateDefaultDownstreamFees({
        depositFeeRateBps: v.depositFeeRateBps,
        cardOpenFeeCents: openCents,
      });
      patchProfile(next);
      Message.success('默认下游费率已更新');
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  return (
    <div className="up-page">
      <PageHeader title="个人设置中心" subtitle="维护登录凭证、个人资料、联系方式与下游费率默认值" />

      {profile?.parent ? (
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          content={`我的上级：${profile.parent.displayName ?? '—'} · ${profile.parent.phone ?? '—'}`}
        />
      ) : null}

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <div className="up-section-card">
            <div className="up-page__title" style={{ fontSize: 16, marginBottom: 12 }}>
              基本联系信息
            </div>
            <Form form={contactForm} layout="vertical">
              <Form.Item label="登录账号">
                <Input value={profile?.username} disabled />
              </Form.Item>
              <Form.Item
                label="主要邮箱"
                field="email"
                rules={[
                  { required: true, message: '邮箱必填' },
                  { match: EMAIL_REGEX, message: '邮箱格式不正确' },
                ]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Form.Item
                label="通知电话（含国际区号）"
                field="phone"
                rules={[{ match: PHONE_INTL_REGEX, message: '请使用 +区号 开头格式' }]}
              >
                <Input placeholder="+86-138-0000-0000" allowClear />
              </Form.Item>
              <Form.Item
                label="显示名"
                field="displayName"
                rules={[{ maxLength: 64, message: '最多 64 字符' }]}
                extra="下级客户可见您的显示名"
              >
                <Input placeholder="如：华东区代理" maxLength={64} />
              </Form.Item>
              <Form.Item
                label="姓名"
                field="legalName"
                rules={[{ maxLength: 128, message: '最多 128 字符' }]}
              >
                <Input placeholder="真实姓名" maxLength={128} />
              </Form.Item>
              <Form.Item
                label="地址"
                field="addressLine"
                rules={[{ maxLength: 255, message: '最多 255 字符' }]}
              >
                <Input placeholder="联系地址" maxLength={255} />
              </Form.Item>
              <Button type="primary" onClick={handleContactSubmit}>
                保存个人资料
              </Button>
            </Form>
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="up-section-card" style={{ marginBottom: 16 }}>
            <div className="up-page__title" style={{ fontSize: 16, marginBottom: 12 }}>
              安全凭证维护
            </div>
            <Form form={pwdForm} layout="vertical">
              <Form.Item
                label="原登录密码"
                field="oldPassword"
                rules={[{ required: true, message: '请输入原密码' }]}
              >
                <Input.Password placeholder="请输入" />
              </Form.Item>
              <Form.Item
                label="新登录密码"
                field="newPassword"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { minLength: 8, message: '至少 8 位' },
                ]}
              >
                <Input.Password placeholder="至少 8 位，含字母与数字" />
              </Form.Item>
              <Form.Item
                label="确认新密码"
                field="confirmPassword"
                rules={[{ required: true, message: '请再次输入' }]}
              >
                <Input.Password placeholder="再次输入" />
              </Form.Item>
              <Button type="primary" status="warning" onClick={handlePasswordSubmit}>
                更新密码
              </Button>
            </Form>
          </div>

          <div className="up-section-card">
            <div className="up-page__title" style={{ fontSize: 16, marginBottom: 12 }}>
              默认下游费率
            </div>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              content={`创建邀请码时将预填以下默认值；不得低于本人费率：入账 ${formatPercent(minRate)}，开卡费 ${formatMoney(minOpen)}`}
            />
            <Form form={feeForm} layout="vertical">
              <Form.Item
                label="默认下游入账费率（bps，100 = 1%）"
                field="depositFeeRateBps"
                rules={[{ required: true, message: '请输入默认入账费率' }]}
              >
                <InputNumber min={minRate} max={10000} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label="默认下游开卡费（USD）"
                field="cardOpenFeeCents"
                rules={[{ required: true, message: '请输入默认开卡费' }]}
              >
                <MoneyInput minDollar={minOpen / 100} />
              </Form.Item>
              <Button type="primary" onClick={handleFeeSubmit}>
                保存默认费率
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </div>
  );
}
